import React, { useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, LoadingButton,
} from '@redpanda/ui';
import { useUnsavedChangesWarning } from '@redpanda/utils';
import { bankAccountService } from '../services/api';
import { BankAccount, BANK_ACCOUNT_TYPES, useBankAccountStoreBase } from '../stores/bankAccountStore';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';

interface BankAccountDialogProps {
  /** 有值＝編輯；沒有＝新增 */
  account?: BankAccount;
  onClose: () => void;
}

interface FormData {
  bankName: string;
  accountName: string;
  accountNumber: string;
  accountType: string;
  currency: string;
  openingBalance: string;
  isActive: string;
  description: string;
}

const CURRENCIES = ['TWD', 'USD', 'JPY', 'CNY', 'EUR', 'HKD'];
const emptyForm = (): FormData => ({
  bankName: '', accountName: '', accountNumber: '', accountType: 'CHECKING', currency: 'TWD', openingBalance: '', isActive: 'true', description: '',
});

/**
 * 新增／編輯銀行帳戶：等伺服器回應才關（同一銀行的帳號不能重複，要標在欄位下方）
 * 目前餘額由後端依「期初餘額＋交易」計算，表單只填期初餘額
 */
const BankAccountDialog: React.FC<BankAccountDialogProps> = ({ account, onClose }) => {
  const { t } = useTranslation();
  const isEditMode = !!account;
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, formState: { errors, isDirty }, reset, setValue, watch, setError } = useForm<FormData>({ defaultValues: emptyForm() });

  useLayoutEffect(() => {
    reset(account ? {
      bankName: account.bankName,
      accountName: account.accountName,
      accountNumber: account.accountNumber,
      accountType: account.accountType,
      currency: account.currency || 'TWD',
      openingBalance: String(Number(account.openingBalance ?? 0)),
      isActive: account.isActive ? 'true' : 'false',
      description: account.description || '',
    } : emptyForm());
  }, [account, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !saving, onClose);

  const onSubmit = async (data: FormData) => {
    const payload = {
      bankName: data.bankName.trim(),
      accountName: data.accountName.trim(),
      accountNumber: data.accountNumber.trim(),
      accountType: data.accountType,
      currency: data.currency,
      openingBalance: Number(data.openingBalance) || 0,
      isActive: data.isActive === 'true',
      description: data.description.trim() || null,
    };
    setSaving(true);
    try {
      const store = useBankAccountStoreBase.getState();
      if (account) {
        const { data: saved } = await bankAccountService.updateBankAccount(account.id, payload);
        store.updateItem(account.id, saved);
        toast.success(t('common.messages.updateSuccess'));
      } else {
        const { data: saved } = await bankAccountService.createBankAccount(payload);
        store.addItem({ ...saved, unreconciledCount: 0 });
        toast.success(t('common.messages.createSuccess'));
      }
      notifyDataChanged('bankAccounts');
      onClose();
    } catch (error: any) {
      if (error?.response?.data?.code === 'BANK_ACCOUNT_EXISTS') {
        setError('accountNumber', { type: 'manual', message: t('errors.BANK_ACCOUNT_EXISTS') });
      } else {
        toast.error(apiErrorMessage(error, account ? 'common.messages.updateFailed' : 'common.messages.createFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  const required = { validate: (value: string) => !!value.trim() || t('common.forms.validation.required') };
  const openingField = register('openingBalance', {
    validate: value => !value || !Number.isNaN(Number(value)) || t('common.forms.validation.invalidAmount'),
  });

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={saving} className="max-w-2xl">
        <DialogHeader
          title={isEditMode ? t('bankAccounts.dialog.editTitle') : t('bankAccounts.dialog.addTitle')}
          onClose={handleClose}
          closeDisabled={saving}
          closeLabel={t('common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5">
            <FormFieldWithError label={t('bankAccounts.dialog.bankName')} required error={errors.bankName?.message} htmlFor="bankName">
              <Input id="bankName" autoFocus={!isEditMode} {...register('bankName', required)} variant={errors.bankName ? 'error' : 'default'} />
            </FormFieldWithError>
            <FormFieldWithError label={t('bankAccounts.dialog.accountName')} required error={errors.accountName?.message} htmlFor="accountName">
              <Input id="accountName" {...register('accountName', required)} variant={errors.accountName ? 'error' : 'default'} />
            </FormFieldWithError>
            <FormFieldWithError label={t('bankAccounts.dialog.accountNumber')} required error={errors.accountNumber?.message} htmlFor="accountNumber">
              <Input id="accountNumber" inputMode="numeric" {...register('accountNumber', required)} variant={errors.accountNumber ? 'error' : 'default'} />
            </FormFieldWithError>
            <FormFieldWithError label={t('bankAccounts.dialog.accountType')} required htmlFor="accountType">
              <CustomSelect
                id="accountType"
                value={watch('accountType')}
                onChange={value => setValue('accountType', value, { shouldDirty: true })}
                options={BANK_ACCOUNT_TYPES.map(type => ({ value: type, label: t(`bankAccounts.types.${type}`) }))}
              />
            </FormFieldWithError>
            <FormFieldWithError label={t('bankAccounts.dialog.currency')} htmlFor="currency">
              <CustomSelect
                id="currency"
                value={watch('currency')}
                onChange={value => setValue('currency', value, { shouldDirty: true })}
                options={CURRENCIES.map(code => ({ value: code, label: code }))}
              />
            </FormFieldWithError>
            <FormFieldWithError
              label={t('bankAccounts.dialog.openingBalance')}
              error={errors.openingBalance?.message}
              helpText={t('bankAccounts.dialog.openingBalanceHint')}
              htmlFor="openingBalance"
            >
              <Input
                id="openingBalance"
                inputMode="decimal"
                className="text-right tabular-nums"
                {...openingField}
                onChange={event => {
                  event.target.value = event.target.value.replace(/[^\d.-]/g, '').replace(/(?!^)-/g, '').replace(/(\..*)\./g, '$1');
                  return openingField.onChange(event);
                }}
                variant={errors.openingBalance ? 'error' : 'default'}
              />
            </FormFieldWithError>
            {isEditMode && (
              <FormFieldWithError label={t('common.table.columns.status')} htmlFor="isActive">
                <CustomSelect
                  id="isActive"
                  value={watch('isActive')}
                  onChange={value => setValue('isActive', value, { shouldDirty: true })}
                  options={[
                    { value: 'true', label: t('bankAccounts.status.active') },
                    { value: 'false', label: t('bankAccounts.status.inactive') },
                  ]}
                />
              </FormFieldWithError>
            )}
            <div className={isEditMode ? '' : 'sm:col-span-2'}>
              <FormFieldWithError label={t('bankAccounts.dialog.description')} htmlFor="description">
                <Input id="description" {...register('description')} />
              </FormFieldWithError>
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={saving} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton type="submit" loading={saving} className={dialogButtonClass.primary}>
              {isEditMode ? t('common.actions.save') : t('common.actions.create')}
            </LoadingButton>
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default BankAccountDialog;
